import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Users, Code, Award } from "lucide-react";

const Home: React.FC = () => {
  const stats = [
    { icon: Users, number: "500+", label: "Happy Clients" },
    { icon: Code, number: "1000+", label: "Projects Completed" },
    { icon: Award, number: "50+", label: "Awards Won" },
  ];

  const features = [
    {
      title: "Custom Web Development",
      description: "Tailored solutions built with modern technologies and best practices.",
      icon: "💻",
    },
    {
      title: "Mobile App Development",
      description: "Native and cross-platform mobile applications for iOS and Android.",
      icon: "📱",
    },
    {
      title: "E-commerce Solutions",
      description: "Complete online store solutions with payment processing and inventory management.",
      icon: "🛒",
    },
    {
      title: "Cloud & DevOps",
      description: "Scalable cloud infrastructure and automated deployment pipelines.",
      icon: "☁️",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white section">
        <div className="container">
          <div className="grid grid-cols-1 gap-12 items-center lg:grid-cols-2">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <h1 className="mb-6 text-5xl font-bold leading-tight lg:text-6xl text-dark-gray">
                Building Digital
                <span className="block text-primary-blue">Excellence</span>
              </h1>
              <p className="mb-8 text-xl leading-relaxed text-medium-gray">
                We transform ideas into powerful digital solutions. From web applications to mobile apps, we deliver cutting-edge technology that
                drives business growth.
              </p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Link to="/contact" className="px-8 py-4 text-lg btn btn-primary">
                  Get Started
                  <ArrowRight size={20} className="ml-2" />
                </Link>
                <Link to="/services" className="px-8 py-4 text-lg btn btn-outline">
                  Our Services
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative"
            >
              <div className="relative p-8 bg-gradient-to-br rounded-3xl border shadow-2xl from-primary-50 to-primary-100 border-primary-200">
                {/* Floating Code Elements */}
                <motion.div
                  animate={{
                    y: [0, -10, 0],
                    rotate: [0, 5, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="flex absolute top-4 right-4 justify-center items-center w-12 h-12 text-sm font-bold text-white rounded-lg shadow-lg bg-primary-500"
                >
                  &lt;/&gt;
                </motion.div>

                <motion.div
                  animate={{
                    y: [0, 15, 0],
                    rotate: [0, -3, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                  className="flex absolute bottom-6 left-6 justify-center items-center w-8 h-8 text-xs font-bold text-white bg-orange-500 rounded-full shadow-lg"
                >
                  ⚡
                </motion.div>

                {/* Main Code Display */}
                <div className="overflow-hidden p-6 bg-gray-900 rounded-2xl border border-gray-700 shadow-xl">
                  {/* Terminal Header */}
                  <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                      <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse" style={{ animationDelay: "0.2s" }}></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: "0.4s" }}></div>
                    </div>
                    <span className="font-mono text-sm text-gray-400">codeflair</span>
                  </div>

                  {/* Animated Code Lines */}
                  <div className="space-y-3 font-mono text-sm">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                      className="flex items-center"
                    >
                      <span className="mr-4 text-blue-400">1</span>
                      <span className="text-purple-400">const</span>
                      <span className="ml-2 text-white">innovation</span>
                      <span className="text-gray-400">=</span>
                      <span className="ml-2 text-green-400">"unlimited"</span>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 }}
                      className="flex items-center"
                    >
                      <span className="mr-4 text-blue-400">2</span>
                      <span className="text-purple-400">const</span>
                      <span className="ml-2 text-white">creativity</span>
                      <span className="text-gray-400">=</span>
                      <span className="ml-2 text-green-400">"boundless"</span>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.9 }}
                      className="flex items-center"
                    >
                      <span className="mr-4 text-blue-400">3</span>
                      <span className="text-gray-400">//</span>
                      <span className="ml-2 text-gray-500">Building the future, one line at a time</span>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.1 }}
                      className="flex items-center"
                    >
                      <span className="mr-4 text-blue-400">4</span>
                      <span className="text-purple-400">return</span>
                      <span className="ml-2 text-white">{`{`}</span>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.3 }}
                      className="flex items-center ml-6"
                    >
                      <span className="mr-4 text-blue-400">5</span>
                      <span className="text-yellow-400">innovation</span>
                      <span className="text-gray-400">,</span>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.5 }}
                      className="flex items-center ml-6"
                    >
                      <span className="mr-4 text-blue-400">6</span>
                      <span className="text-yellow-400">creativity</span>
                      <span className="text-gray-400">,</span>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.7 }}
                      className="flex items-center ml-6"
                    >
                      <span className="mr-4 text-blue-400">7</span>
                      <span className="text-yellow-400">excellence</span>
                      <span className="text-gray-400">:</span>
                      <span className="ml-2 text-green-400">true</span>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.9 }}
                      className="flex items-center"
                    >
                      <span className="mr-4 text-blue-400">8</span>
                      <span className="text-white">{`}`}</span>
                    </motion.div>
                  </div>

                  {/* Animated Cursor */}
                  <motion.div
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="inline-block ml-2 w-2 h-5 bg-primary-500"
                  />
                </div>

                {/* Floating Particles */}
                <motion.div
                  animate={{
                    y: [0, -20, 0],
                    opacity: [0.3, 0.8, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute top-12 left-12 w-2 h-2 rounded-full bg-primary-400"
                />
                <motion.div
                  animate={{
                    y: [0, -15, 0],
                    opacity: [0.2, 0.6, 0.2],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.5,
                  }}
                  className="absolute bottom-16 right-16 w-1.5 h-1.5 bg-orange-400 rounded-full"
                />
                <motion.div
                  animate={{
                    y: [0, -25, 0],
                    opacity: [0.4, 0.7, 0.4],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                  className="absolute top-20 left-20 w-1 h-1 rounded-full bg-primary-300"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white section">
        <div className="container">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    <stat.icon size={48} className="text-primary-blue" />
                  </div>
                  <h3 className="mb-2 text-4xl font-bold text-dark-gray">{stat.number}</h3>
                  <p className="text-medium-gray">{stat.label}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section bg-light-gray">
        <div className="container">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold text-dark-gray">Why Choose Codeflair?</h2>
            <p className="mx-auto max-w-2xl text-xl text-medium-gray">
              We combine technical expertise with creative innovation to deliver solutions that exceed expectations.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="p-6 bg-white rounded-xl transition-shadow shadow-medium hover:shadow-heavy">
                  <div className="mb-4 text-4xl">{feature.icon}</div>
                  <h3 className="mb-3 text-xl font-semibold text-dark-gray">{feature.title}</h3>
                  <p className="text-medium-gray">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-gradient-primary">
        <div className="container text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h2 className="mb-4 text-4xl font-bold text-white">Ready to Start Your Project?</h2>
            <p className="mx-auto mb-8 max-w-2xl text-xl text-gray-400">
              Let's discuss how we can help bring your vision to life with cutting-edge technology and exceptional design.
            </p>
            <Link to="/contact" className="px-8 py-4 text-lg btn btn-white">
              Get Free Consultation
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
